<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      @click.self="close"
    >
      <div class="bg-console-panel border border-console-border rounded-xl w-full max-w-2xl mx-4 overflow-hidden">
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-console-border">
          <h2 class="text-lg font-bold text-white flex items-center gap-2">
            <span class="material-symbols-outlined text-primary">content_cut</span>
            Trim Audio
          </h2>
          <button
            @click="close"
            class="p-1 hover:bg-white/10 rounded-lg transition-colors"
          >
            <span class="material-symbols-outlined text-white/60">close</span>
          </button>
        </div>

        <!-- Content -->
        <div class="p-4 space-y-4">
          <!-- Clip Info -->
          <div class="text-sm text-white/60">
            <span class="text-white font-medium">{{ clip?.title }}</span>
            <span class="mx-2">•</span>
            <span>{{ formatDuration(originalDuration) }}</span>
          </div>

          <!-- Waveform Container -->
          <div class="relative">
            <div
              ref="waveformRef"
              class="w-full h-32 bg-background-dark rounded-lg overflow-hidden"
            ></div>
            
            <!-- Loading overlay -->
            <div
              v-if="isLoading"
              class="absolute inset-0 flex items-center justify-center bg-background-dark/80"
            >
              <div class="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
            </div>
          </div>

          <!-- Time Display -->
          <div class="flex items-center justify-between text-sm">
            <div class="flex items-center gap-4">
              <div>
                <span class="text-white/60">Start:</span>
                <span class="ml-2 text-primary font-mono">{{ formatDuration(regionStart) }}</span>
              </div>
              <div>
                <span class="text-white/60">End:</span>
                <span class="ml-2 text-primary font-mono">{{ formatDuration(regionEnd) }}</span>
              </div>
            </div>
            <div>
              <span class="text-white/60">New duration:</span>
              <span class="ml-2 text-white font-mono">{{ formatDuration(regionEnd - regionStart) }}</span>
            </div>
          </div>

          <!-- Instructions -->
          <p class="text-xs text-white/40">
            Drag the pink region handles to select the portion to keep. Click Preview to hear the selection.
          </p>

          <!-- Playback Controls -->
          <div class="flex items-center gap-2">
            <button
              @click="playOriginal"
              class="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-colors"
              :disabled="isLoading"
            >
              <span class="material-symbols-outlined text-base">play_arrow</span>
              Play Full
            </button>
            <button
              @click="playRegion"
              class="flex items-center gap-1 px-3 py-1.5 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg text-sm transition-colors"
              :disabled="isLoading"
            >
              <span class="material-symbols-outlined text-base">play_arrow</span>
              Preview Selection
            </button>
            <button
              @click="stopPlayback"
              class="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-colors"
              :disabled="isLoading"
            >
              <span class="material-symbols-outlined text-base">stop</span>
              Stop
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 p-4 border-t border-console-border bg-background-dark/50">
          <button
            @click="close"
            class="px-4 py-2 text-sm text-white/60 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            @click="saveAsNewClip"
            :disabled="isSaving || !hasChanges"
            class="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium transition-colors"
          >
            <span v-if="isSaving && saveMode === 'saveAs'" class="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
            <span class="material-symbols-outlined text-base" v-else>add_circle</span>
            Save As New
          </button>
          <button
            @click="saveTrim"
            :disabled="isSaving || !hasChanges"
            class="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium transition-colors"
          >
            <span v-if="isSaving && saveMode === 'overwrite'" class="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
            <span class="material-symbols-outlined text-base" v-else>content_cut</span>
            {{ isSaving && saveMode === 'overwrite' ? 'Trimming...' : 'Overwrite' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted, onMounted, nextTick } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.js';
import { useClipsStore } from '../stores/clipsStore';
import type { AudioClip } from '../api/types';

const props = defineProps<{
  clip: AudioClip;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'trimmed', clip: AudioClip): void;
}>();

const clipsStore = useClipsStore();

const waveformRef = ref<HTMLElement | null>(null);
const wavesurfer = ref<WaveSurfer | null>(null);
const regionsPlugin = ref<any>(null);
const activeRegion = ref<any>(null);

const isLoading = ref(false);
const isSaving = ref(false);
const saveMode = ref<'overwrite' | 'saveAs' | null>(null);
const originalDuration = ref(0);
const regionStart = ref(0);
const regionEnd = ref(0);

const hasChanges = computed(() => {
  return regionStart.value > 0.01 || Math.abs(regionEnd.value - originalDuration.value) > 0.01;
});

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 10);
  return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`;
}

async function initWaveSurfer() {
  if (!waveformRef.value || !props.clip) return;

  isLoading.value = true;

  // Cleanup existing instance
  if (wavesurfer.value) {
    wavesurfer.value.destroy();
  }

  // Create regions plugin
  regionsPlugin.value = RegionsPlugin.create();

  // Create WaveSurfer instance
  wavesurfer.value = WaveSurfer.create({
    container: waveformRef.value,
    waveColor: 'rgba(255, 255, 255, 0.3)',
    progressColor: '#e60077',
    cursorColor: '#ffffff',
    cursorWidth: 2,
    height: 128,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    normalize: true,
    plugins: [regionsPlugin.value],
  });

  // Load audio via streaming endpoint
  const audioUrl = `/api/v1/clips/${props.clip.id}/stream`;
  
  try {
    await wavesurfer.value.load(audioUrl);
    
    originalDuration.value = wavesurfer.value.getDuration();
    regionEnd.value = originalDuration.value;

    // Add initial region covering entire clip
    activeRegion.value = regionsPlugin.value.addRegion({
      start: 0,
      end: originalDuration.value,
      color: 'rgba(230, 0, 119, 0.3)',
      drag: false,
      resize: true,
    });

    // Listen for region updates
    activeRegion.value.on('update', () => {
      regionStart.value = activeRegion.value.start;
      regionEnd.value = activeRegion.value.end;
    });

    activeRegion.value.on('update-end', () => {
      regionStart.value = activeRegion.value.start;
      regionEnd.value = activeRegion.value.end;
    });

  } catch (error) {
    console.error('Failed to load audio:', error);
  } finally {
    isLoading.value = false;
  }
}

function playOriginal() {
  wavesurfer.value?.play();
}

function playRegion() {
  if (!wavesurfer.value || !activeRegion.value) return;
  
  // Play from region start
  wavesurfer.value.setTime(regionStart.value);
  wavesurfer.value.play();
  
  // Set up listener to stop at region end
  const checkEnd = () => {
    if (wavesurfer.value && wavesurfer.value.getCurrentTime() >= regionEnd.value) {
      wavesurfer.value.pause();
      wavesurfer.value.un('audioprocess', checkEnd);
    }
  };
  wavesurfer.value.on('audioprocess', checkEnd);
}

function stopPlayback() {
  wavesurfer.value?.stop();
}

async function saveTrim() {
  if (!props.clip || isSaving.value) return;

  isSaving.value = true;
  saveMode.value = 'overwrite';

  try {
    const updatedClip = await clipsStore.trimClip(
      props.clip.id,
      regionStart.value,
      regionEnd.value
    );
    
    emit('trimmed', updatedClip);
    close();
  } catch (error) {
    console.error('Failed to trim clip:', error);
    alert('Failed to trim clip. Please try again.');
  } finally {
    isSaving.value = false;
    saveMode.value = null;
  }
}

async function saveAsNewClip() {
  if (!props.clip || isSaving.value) return;

  isSaving.value = true;
  saveMode.value = 'saveAs';

  try {
    const newClip = await clipsStore.trimAndSaveAs(
      props.clip.id,
      regionStart.value,
      regionEnd.value
    );
    
    emit('trimmed', newClip);
    close();
  } catch (error) {
    console.error('Failed to save trimmed clip:', error);
    alert('Failed to save trimmed clip. Please try again.');
  } finally {
    isSaving.value = false;
    saveMode.value = null;
  }
}

function close() {
  stopPlayback();
  emit('close');
}

// Initialize wavesurfer when component mounts
onMounted(async () => {
  await nextTick();
  initWaveSurfer();
});

// Cleanup on unmount
onUnmounted(() => {
  if (wavesurfer.value) {
    wavesurfer.value.destroy();
  }
});
</script>
